import {
  Menu,
  MenuItem,
  Flex,
  Text,
  DrawerHeader,
  DrawerBody,
  Box,
  HStack,
  Button,
} from '@chakra-ui/react';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '$/components/common/Icon';
import { ProMenuItem } from '$/components/core/Collection/CollectionFilter/components/ProMenuItem';
import { useFilterOptions } from '$/hooks/data/useFilterOptions';
import { useSwipeHook } from '$/hooks/useSwipeHook';
import { useCollectionStore } from '$/stores/useCollectionStore';

interface Props {
  selectedSubFilter: string;
  closeFilterDrawer: () => void;
  setSubFilter: (filterSearchName: string | null) => void;
  closeOnSelect?: boolean;
}

export const MobileSubFilterPage: FC<Props> = ({
  selectedSubFilter,
  closeFilterDrawer,
  setSubFilter,
  closeOnSelect = true,
}) => {
  const { t } = useTranslation();

  const { data: possibleFilter } = useFilterOptions();

  const swipeEvents = useSwipeHook({
    onSwipeVertical: (direction) => direction === 'down' && closeFilterDrawer(),
  });

  if (!possibleFilter) return undefined;

  const filter = [...possibleFilter.general, ...possibleFilter.additional].find(
    (x) => x.searchField === selectedSubFilter,
  )!;

  const isNoFilterSelected = useCollectionStore
    .getState()
    .getActiveInCategory(filter.searchField);

  return (
    <Box>
      <DrawerHeader px='3' pt='5' pb='0' {...swipeEvents}>
        <HStack justify='space-between'>
          <Button
            leftIcon={
              <Icon
                transform={'rotate(180deg)'}
                icon='arrow_right'
                width='13px'
                mr='2px'
              />
            }
            onClick={() => {
              if (selectedSubFilter === 'collection') {
                if (closeOnSelect) closeFilterDrawer();
              }
              setSubFilter(null);
            }}
            variant='ghost'
          >
            <Text fontSize='md'>{filter.displayName}</Text>
          </Button>

          <Button
            h='36px'
            fontSize='sm'
            onClick={() => closeFilterDrawer()}
            variant='text'
          >
            {t('general.cancel')}
          </Button>
        </HStack>
      </DrawerHeader>

      <DrawerBody p='3'>
        <Menu>
          <MenuItem
            p='4'
            fontWeight='bold'
            bg={!isNoFilterSelected ? 'secondaryBackground' : 'background'}
            borderRadius='4px'
            onClick={() => {
              useCollectionStore
                .getState()
                .resetAppliedFilterOption([filter.searchField]);

              if (closeOnSelect) closeFilterDrawer();
              if (!closeOnSelect) setSubFilter(null);
            }}
          >
            <Flex align='center' justify='space-between' w='full'>
              <Text fontSize='sm'>{t('dashboard.collection.show')}</Text>
              <Text color='lighterText' fontSize='xs'>
                {filter?.items.reduce((acc, item) => acc + item.count, 0)}
              </Text>
            </Flex>
          </MenuItem>
          {filter?.items.map((item) => {
            return (
              <MenuItem
                key={item.value}
                px='3'
                py='4'
                fontWeight='bold'
                bg={
                  useCollectionStore
                    .getState()
                    .isFilterActive(filter.searchField, item.value)
                    ? 'secondaryBackground'
                    : 'background'
                }
                borderRadius='4px'
                onClick={() => {
                  useCollectionStore.getState().toggleAppliedFilterOption({
                    option: filter.searchField,
                    value: item.value,
                  });

                  if (closeOnSelect) closeFilterDrawer();
                  if (!closeOnSelect) setSubFilter(null);
                }}
              >
                <Flex align='center' justify='space-between' w='full'>
                  <Text fontSize='sm'>{item.displayName}</Text>
                  <Text color='lighterText' fontSize='xs'>
                    {item.count}
                  </Text>
                </Flex>
              </MenuItem>
            );
          })}
          <ProMenuItem name={filter.displayName} isMobile />
        </Menu>
      </DrawerBody>
    </Box>
  );
};
